import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";

// components
import { RightArrow } from "components/common";

// utils
import { moneyFormatter } from "utils";
import {
  SERVICE_TYPE_ORDER,
  SERVICE_TYPE_META,
  VOUCHER_SUMMARY_KEYS,
  VOUCHER_REFUND_CANCEL_KEY,
  REFUND_CANCEL_META,
  toRefundCancelAmount,
} from "constants/salesServiceType";

const TodaysVouchers = ({ voucherData, isAll, ...props }) => {
  const navigate = useNavigate();

  // 분류 6종은 constants/salesServiceType의 단일 정의에서 파생한다.
  // 예전에는 여기서 라벨·색·서버 필드명을 하드코딩했고, 필드명은
  // `item + "SalesAmount"` 규칙을 따랐다 — 신규 필드(transferCommissionAmount,
  // etcAmount)가 그 규칙을 깨므로 명시 매핑표(VOUCHER_SUMMARY_KEYS)를 쓴다.
  const sections = SERVICE_TYPE_ORDER.map((code) => ({
    key: code,
    label: SERVICE_TYPE_META[code].label,
    color: SERVICE_TYPE_META[code].colors.base,
    valuePercent: voucherData[VOUCHER_SUMMARY_KEYS[code].percent],
    valueAmount: voucherData[VOUCHER_SUMMARY_KEYS[code].amount],
  }));

  // 환불·취소는 분류가 아니라 6분류 합계 밖의 값이다.
  // 서버가 양수 크기로 주므로 음수 부호는 여기서 붙인다.
  const refundCancelAmount = voucherData[VOUCHER_REFUND_CANCEL_KEY];
  const hasRefundCancel =
    refundCancelAmount !== undefined && refundCancelAmount !== null;

  const renderSections = () => {
    return sections.map((section) => {
      return (
        <StatsWrapper className="bottom-padding" key={section.key}>
          <div>
            <Dot color={section.color} />
            <StatTitle>{section.label}</StatTitle>
            <Percent color={section.color}>
              &middot; {section.valuePercent ? section.valuePercent : 0}%
            </Percent>
          </div>
          <StatAmount>{moneyFormatter(section.valueAmount)}</StatAmount>
        </StatsWrapper>
      );
    });
  };

  return (
    <MainWrapper
      onClick={() =>
        isAll ? navigate("/sales/branch/voucher") : navigate("/sales/voucher")
      }
    >
      <CardTitleWrapper>
        <Title>오늘의 이용권 종류별 매출</Title>
        <RightArrow size="S" />
      </CardTitleWrapper>

      {renderSections()}

      {/* 구분선 아래 별도 줄. 6줄 + 별도 줄이지, 7줄이 아니다 */}
      {hasRefundCancel && (
        <>
          <RefundDivider />
          <StatsWrapper>
            <div>
              <Dot color={REFUND_CANCEL_META.color} />
              <StatTitle>{REFUND_CANCEL_META.label}</StatTitle>
            </div>
            <RefundAmount>
              {moneyFormatter(toRefundCancelAmount(refundCancelAmount))}
            </RefundAmount>
          </StatsWrapper>
        </>
      )}
    </MainWrapper>
  );
};

const MainWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 12px;
  padding: 24px 24px 26px 24px;
  border-radius: 12px;
  background-color: ${(props) => props.theme.colors.cardBackground};
`;

const CardTitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.p`
  font-weight: bold;
  font-size: 18px;
  line-height: 24px;
  color: ${(props) => props.theme.colors.primaryTextColor};
`;

const StatsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  &.bottom-padding {
    padding-bottom: 12px;
  }

  div {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
  }
`;

const Percent = styled.span`
  font-size: 14px;
  line-height: 20px;
  margin-left: 4px;
  color: ${(props) => props.color};
`;

const StatTitle = styled.p`
  font-family: Pretendard;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  color: ${(props) => props.theme.colors.primaryTextColor};
`;

const Dot = styled.span`
  height: 8px;
  width: 8px;
  background-color: ${(props) => props.color};
  border-radius: 50%;
  margin-right: 6px;
`;

const StatAmount = styled.p`
  font-feature-settings: "tnum" on, "lnum" on;
  font-size: 16px;
  line-height: 24px;
  color: ${(props) => props.theme.colors.primaryTextColor};
`;

const RefundDivider = styled.div`
  height: 1px;
  background-color: #eaedf2;
  margin-bottom: 12px;
`;

const RefundAmount = styled(StatAmount)`
  color: ${REFUND_CANCEL_META.color};
`;

TodaysVouchers.propTypes = {
  voucherData: PropTypes.object,
  onClick: PropTypes.func,
};

export default TodaysVouchers;
